import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BrainCircuit,
  Monitor,
  Smartphone,
  BarChart3,
  ShoppingBag,
  ExternalLink,
} from 'lucide-react';
import TiltCard from '@/components/brand/TiltCard';
import { PrimaryButton } from '@/components/brand/Buttons';
import { fadeUp, viewportOnce } from '@/lib/motion';
import { HOME } from '@/constants/testIds';

const DEFAULT_CATEGORIES = [
  { id: 'ai',     label: 'AI & Machine Learning',    Icon: BrainCircuit },
  { id: 'web',    label: 'Web Development',           Icon: Monitor },
  { id: 'mobile', label: 'Mobile Development',        Icon: Smartphone },
  { id: 'biz',    label: 'Business Analytics',        Icon: BarChart3 },
  { id: 'ecom',   label: 'E-commerce & UI/UX',        Icon: ShoppingBag },
];

const CATEGORY_ICON_MAP = { BrainCircuit, Monitor, Smartphone, BarChart3, ShoppingBag };

const DEFAULT_EYEBROW = 'Our Work';
const DEFAULT_HEADING = 'Our Portfolio';
const DEFAULT_SUBHEAD = 'A curated selection of digital products, AI solutions, and platforms we have built for clients across the globe.';

const DEFAULT_PROJECTS = {
  ai: [
    { title: 'Clinical Copilot',        tag: 'Healthcare LLM',   img: '/images/portfolio/fallback/photo-1576091160550-2173dba999ef.jpg' },
    { title: 'Retail Recommender',      tag: 'Recommendation AI', img: '/images/portfolio/fallback/photo-1556742393-d75f468bfcb0.jpg' },
    { title: 'Vision QC Engine',        tag: 'Computer Vision',   img: '/images/portfolio/fallback/photo-1581091226825-a6a2a5aee158.jpg' },
    { title: 'Compliance Copilot',      tag: 'Fintech NLP',       img: '/images/portfolio/fallback/photo-1554224155-6726b3ff858f.jpg' },
    { title: 'Document AI',             tag: 'NLP · RAG',         img: '/images/portfolio/fallback/photo-1485796826113-174aa68fd81b.jpg' },
    { title: 'Predictive Maintenance',  tag: 'ML Forecasting',    img: '/images/portfolio/fallback/photo-1518770660439-4636190af475.jpg' },
  ],
  web: [
    { title: 'B2B Marketplace',   tag: 'Next.js · Stripe',  img: '/images/portfolio/fallback/photo-1460925895917-afdab827c52f.jpg' },
    { title: 'Realtor Hub',       tag: 'React · Node',      img: '/images/portfolio/fallback/photo-1486406146926-c627a92ad1ab.jpg' },
    { title: 'EdTech Portal',     tag: 'TypeScript',        img: '/images/portfolio/fallback/photo-1522202176988-66273c2fd55f.jpg' },
    { title: 'SaaS Dashboard',    tag: 'Tailwind · API',    img: '/images/portfolio/fallback/photo-1551288049-bebda4e38f71.jpg' },
    { title: 'Hospitality Portal',tag: 'Booking System',    img: '/images/portfolio/fallback/photo-1551882547-ff40c63fe5fa.jpg' },
    { title: 'Investor Hub',      tag: 'Finance Platform',  img: '/images/portfolio/fallback/photo-1611974789855-9c2a0a7236a3.jpg' },
  ],
  mobile: [
    { title: 'HealthOne', tag: 'Flutter',       img: '/images/portfolio/fallback/photo-1576765607924-3f7b8410a787.jpg' },
    { title: 'BankPay',   tag: 'React Native',  img: '/images/portfolio/fallback/photo-1563986768609-322da13575f3.jpg' },
    { title: 'TravelGo',  tag: 'Swift',         img: '/images/portfolio/fallback/photo-1488646953014-85cb44e25828.jpg' },
    { title: 'FitTrack',  tag: 'Kotlin',        img: '/images/portfolio/fallback/photo-1571019613454-1cb2f99b2d8b.jpg' },
    { title: 'ChefMate',  tag: 'React Native',  img: '/images/portfolio/fallback/photo-1556909114-f6e7ad7d3136.jpg' },
    { title: 'PetPal',    tag: 'Flutter',       img: '/images/portfolio/fallback/photo-1450778869180-41d0601e046e.jpg' },
  ],
  biz: [
    { title: 'Demand Forecast', tag: 'BI · ML',         img: '/images/portfolio/fallback/photo-1551288049-bebda4e38f71.jpg' },
    { title: 'Supply Insights', tag: 'Power BI',        img: '/images/portfolio/fallback/photo-1543286386-713bdd548da4.jpg' },
    { title: 'Sales Lens',      tag: 'Looker',          img: '/images/portfolio/fallback/photo-1504384308090-c894fdcc538d.jpg' },
    { title: 'Ops Pulse',       tag: 'Tableau',         img: '/images/portfolio/fallback/photo-1460925895917-afdab827c52f.jpg' },
    { title: 'Marketing Mix',   tag: 'Analytics',       img: '/images/portfolio/fallback/photo-1551434678-e076c223a692.jpg' },
    { title: 'Revenue OS',      tag: 'Decision Engine', img: '/images/portfolio/fallback/photo-1611974789855-9c2a0a7236a3.jpg' },
  ],
  ecom: [
    { title: 'Style House',     tag: 'Shopify',        img: '/images/portfolio/fallback/photo-1483985988355-763728e1935b.jpg' },
    { title: 'Auctora',         tag: 'Magento',        img: '/images/portfolio/fallback/photo-1607082348824-0a96f2a4b9da.jpg' },
    { title: 'Real Plot',       tag: 'WooCommerce',    img: '/images/portfolio/fallback/photo-1560518883-ce09059eeffa.jpg' },
    { title: 'Heyway',          tag: 'Headless CMS',   img: '/images/portfolio/fallback/photo-1556742111-a301076d9d18.jpg' },
    { title: 'Acme Storefront', tag: 'Next Commerce',  img: '/images/portfolio/fallback/photo-1491933382434-500287f9b54b.jpg' },
    { title: 'Lumen UX',        tag: 'UI/UX Design',   img: '/images/portfolio/fallback/photo-1561070791-2526d30994b5.jpg' },
  ],
};

export default function Portfolio({ data, items, categories: liveCategories }) {
  const eyebrow = data?.eyebrow || DEFAULT_EYEBROW;
  const heading = data?.heading || DEFAULT_HEADING;
  const subhead  = data?.subhead  || DEFAULT_SUBHEAD;
  const viewAllLabel = data?.view_all_label || 'View All Work';

  const categories =
    Array.isArray(liveCategories) && liveCategories.length
      ? liveCategories.map((c) => ({
          id: c.category_id || c.id,
          label: c.label,
          Icon: CATEGORY_ICON_MAP[c.icon] || BrainCircuit,
        }))
      : DEFAULT_CATEGORIES;

  const projects = (() => {
    if (Array.isArray(items) && items.length) {
      const grouped = {};
      items.forEach((p) => {
        const key = p.category || 'ai';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({ title: p.title, tag: p.tag, img: p.img });
      });
      categories.forEach((c) => {
        if (!grouped[c.id]) grouped[c.id] = DEFAULT_PROJECTS[c.id] || [];
      });
      return grouped;
    }
    return DEFAULT_PROJECTS;
  })();

  const firstId = categories[0]?.id || 'ai';
  const [active, setActive] = useState(firstId);

  return (
    <section id="portfolio" className="py-16 bg-white">
      <div className="container mx-auto">

        <SectionHeading
          title={heading}
          eyebrow={eyebrow}
          subtitle={subhead}
          align="center"
          className="mb-10"
        />

        {/* ── Tab bar ── */}
        <div
          data-testid={HOME.portfolioTabs}
          className="relative border-b border-hairline mb-8 overflow-x-auto"
        >
          <div className="flex items-center gap-1 sm:gap-2 min-w-max mx-auto justify-center">
            {categories.map((c) => {
              const Icon = c.Icon;
              const isActive = active === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  className={`relative px-4 sm:px-5 py-3 inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
                    isActive ? 'text-navy-deep' : 'text-slatebody hover:text-navy'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-[#C9962E]' : 'text-current'}`} />
                  {c.label}
                  {isActive && (
                    <motion.span
                      layoutId="portfolio-underline"
                      className="absolute left-2 right-2 -bottom-px h-[3px] rounded-full bg-gold-gradient"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Project grid ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, filter: 'blur(8px)', y: 12 }}
            animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
            exit={{ opacity: 0, filter: 'blur(8px)', y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {projects[active]?.map((p) => (
              <Link
                key={p.title}
                to="/portfolio"
                aria-label={`View project — ${p.title}`}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E]/60 rounded-2xl"
              >
                {/* Outer motion wrapper for variant propagation */}
                <motion.div
                  whileHover="hovered"
                  initial="rest"
                  animate="rest"
                  className="h-full"
                >
                  <TiltCard className="group rounded-2xl overflow-hidden bg-white border border-hairline shadow-card hover:shadow-soft transition-shadow h-full">

                    {/* Image area */}
                    <div className="relative h-56 overflow-hidden">
                      {/* Image zooms on hover */}
                      <motion.img
                        src={p.img}
                        alt={p.title}
                        className="w-full h-full object-cover"
                        variants={{
                          rest:    { scale: 1 },
                          hovered: { scale: 1.1 },
                        }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      />

                      {/* Always-on subtle gradient at bottom */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B35]/40 via-transparent to-transparent" />

                      {/* Tag pill */}
                      <span className="absolute top-3 left-3 text-[10px] font-semibold tracking-wide bg-white/90 backdrop-blur-sm text-navy px-2.5 py-1 rounded-full z-10">
                        {p.tag}
                      </span>

                      {/* Hover overlay — slides up */}
                      <motion.div
                        className="absolute inset-0 flex flex-col justify-end p-5 bg-gradient-to-t from-[#0B1B35] via-[#0B1B35]/85 to-[#0B1B35]/10"
                        variants={{
                          rest:    { opacity: 0 },
                          hovered: { opacity: 1 },
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.div
                          variants={{
                            rest:    { y: 20, opacity: 0 },
                            hovered: { y: 0,  opacity: 1 },
                          }}
                          transition={{ duration: 0.32, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
                        >
                          <p className="text-white font-bold text-base leading-snug mb-2">
                            {p.title}
                          </p>
                          <span className="inline-flex items-center gap-1.5 text-[#E8C25A] text-xs font-semibold">
                            View Project
                            <motion.span
                              variants={{
                                rest:    { x: 0 },
                                hovered: { x: 4 },
                              }}
                              transition={{ duration: 0.25 }}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </motion.span>
                          </span>
                        </motion.div>
                      </motion.div>
                    </div>

                    {/* Card footer */}
                    <div className="px-5 py-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-navy truncate">{p.title}</h4>
                        <span className="text-[11px] text-slatebody">{p.tag}</span>
                      </div>

                      {/* Gold circle arrow — fills on hover */}
                      <motion.div
                        className="shrink-0 h-9 w-9 rounded-full border-2 inline-flex items-center justify-center"
                        variants={{
                          rest:    { backgroundColor: 'rgba(248,250,252,1)', borderColor: 'rgba(226,232,240,1)' },
                          hovered: { backgroundColor: '#C9962E',             borderColor: '#C9962E' },
                        }}
                        transition={{ duration: 0.25 }}
                      >
                        <motion.div
                          variants={{
                            rest:    { color: '#1e3a5f', x: 0 },
                            hovered: { color: '#ffffff', x: 2 },
                          }}
                          transition={{ duration: 0.25 }}
                        >
                          <ArrowRight className="h-3.5 w-3.5" />
                        </motion.div>
                      </motion.div>
                    </div>

                  </TiltCard>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* ── Single centered CTA — PrimaryButton with full sheen animation ── */}
        <div className="mt-12 flex justify-center">
          <Link to="/portfolio">
            <PrimaryButton icon={<ArrowRight className="h-4 w-4" />}>
              {viewAllLabel}
            </PrimaryButton>
          </Link>
        </div>

      </div>
    </section>
  );
}
